import React, { useState } from 'react';
import { AgentPublicInfo, Message } from '../types';
import { ChatView } from './ChatView';
import { RecorderButton } from './RecorderButton';

type SessionState = 'idle' | 'recording' | 'processing' | 'playing' | 'error' | 'listening';

interface Props {
  agentInfo: AgentPublicInfo;
  sessionId?: string | null;
  messages: Message[];
  state: SessionState;
  error: string | null;
  onPressStart: () => void;
  onPressEnd: () => void;
  onSendText: (text: string) => void;
  onClear: () => void;
  onDisconnect: () => void;
  streamingMode?: boolean;
  textOnly?: boolean;
  onSwitchMode?: () => void;
  muted?: boolean;
  onToggleMute?: () => void;
}

export function AgentChat({
  agentInfo,
  sessionId,
  messages,
  state,
  error,
  onPressStart,
  onPressEnd,
  onSendText,
  onClear,
  onDisconnect,
  streamingMode,
  textOnly,
  onSwitchMode,
  muted,
  onToggleMute,
}: Props) {
  const [textInput, setTextInput] = useState('');
  const canSend = (state === 'idle' || state === 'listening') && textInput.trim().length > 0;

  const handleSend = () => {
    if (!canSend) return;
    onSendText(textInput.trim());
    setTextInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.agentBar}>
        <div>
          <strong>{agentInfo.name}</strong>
          {agentInfo.description && (
            <span style={styles.desc}> — {agentInfo.description}</span>
          )}
          {streamingMode && (
            <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#2563eb', fontWeight: 600 }}>
              STREAMING
            </span>
          )}
          {textOnly && (
            <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#e0e7ff', color: '#4338ca', fontWeight: 600 }}>
              TEXT
            </span>
          )}
          {sessionId && (
            <span style={{ marginLeft: 8, fontSize: 10, color: '#999', fontFamily: 'monospace' }} title="Session ID — use it on the connect screen to continue this conversation">
              session: {sessionId}
            </span>
          )}
        </div>
        <div style={styles.barActions}>
          {onSwitchMode && (
            <button style={styles.smallBtn} onClick={onSwitchMode}>
              {textOnly ? 'Voice' : 'Text'}
            </button>
          )}
          {messages.length > 0 && (
            <button style={styles.smallBtn} onClick={onClear}>Clear</button>
          )}
          <button style={{ ...styles.smallBtn, color: '#dc2626', borderColor: '#fca5a5' }} onClick={onDisconnect}>
            Disconnect
          </button>
        </div>
      </div>

      <ChatView messages={messages} textOnly={textOnly} />

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.footer}>
        <div style={styles.textInputRow}>
          <input
            style={styles.textInput}
            type="text"
            placeholder="Type a message..."
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={state !== 'idle' && state !== 'listening'}
          />
          <button
            style={{
              ...styles.sendBtn,
              opacity: canSend ? 1 : 0.4,
              cursor: canSend ? 'pointer' : 'default',
            }}
            onClick={handleSend}
            disabled={!canSend}
          >
            Send
          </button>
        </div>
        {!textOnly &&
          (streamingMode ? (
            <StreamingIndicator state={state} muted={muted} onToggleMute={onToggleMute} />
          ) : (
            <RecorderButton
              state={state === 'listening' ? 'idle' : state}
              onPressStart={onPressStart}
              onPressEnd={onPressEnd}
            />
          ))}
      </div>
    </div>
  );
}

function StreamingIndicator({
  state,
  muted,
  onToggleMute,
}: {
  state: SessionState;
  muted?: boolean;
  onToggleMute?: () => void;
}) {
  const isListening = state === 'listening';
  const isProcessing = state === 'processing';
  const isPlaying = state === 'playing';

  let label = 'Connecting...';
  let color = '#888';
  let icon = '...';
  if (muted) {
    label = 'Muted';
    color = '#dc2626';
    icon = '\uD83D\uDD07';
  } else if (isListening) {
    label = 'Listening';
    color = '#16a34a';
    icon = '\uD83C\uDF99\uFE0F';
  } else if (isProcessing) {
    label = 'Thinking...';
    color = '#d97706';
    icon = '\u2699\uFE0F';
  } else if (isPlaying) {
    label = 'Speaking';
    color = '#2563eb';
    icon = '\uD83D\uDD0A';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '8px 0', minWidth: 80 }}>
      <button
        onClick={onToggleMute}
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: `2px solid ${color}`,
          background: muted ? '#fee2e2' : isListening ? '#f0fdf4' : '#fff',
          fontSize: 20,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s',
          boxShadow: isListening && !muted ? `0 0 0 4px rgba(22,163,106,0.15)` : 'none',
        }}
      >
        {icon}
      </button>
      <span style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
  agentBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 20px', borderBottom: '1px solid #eee', fontSize: 14,
  },
  desc: { color: '#888', fontSize: 13 },
  barActions: { display: 'flex', gap: 8 },
  smallBtn: {
    background: 'none', border: '1px solid #ddd', borderRadius: 8,
    padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#666',
  },
  error: {
    margin: '0 20px', padding: '10px 14px', background: '#fff0f0',
    border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13,
  },
  footer: {
    borderTop: '1px solid #eee', display: 'flex', alignItems: 'center',
    padding: '12px 20px', gap: 12,
  },
  textInputRow: {
    display: 'flex', flex: 1, gap: 8,
  },
  textInput: {
    flex: 1, padding: '8px 12px', borderRadius: 8,
    border: '1px solid #ddd', fontSize: 14, outline: 'none',
  },
  sendBtn: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 500,
  },
};
